import type { AssigneeOption } from '@erp/shared';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { X } from 'lucide-react';
import { useId, useMemo, useRef, useState } from 'react';
import { assigneesQuery } from '@/lib/queries';
import { Avatar } from './ui';

function useOptions() {
  const q = useQuery(assigneesQuery);
  return q.data ?? [];
}

function matches(o: AssigneeOption, text: string) {
  const t = text.trim().toLowerCase();
  if (!t) return true;
  return `${o.fullName} ${o.departmentName ?? ''} ${o.positionName ?? ''}`.toLowerCase().includes(t);
}

/**
 * Выбор сотрудников с поиском. `multiple` — несколько человек (соисполнители, наблюдатели).
 * Список раскрывается при фокусе; стрелки и Enter работают с клавиатуры.
 */
export function UserPicker({
  value,
  onChange,
  multiple,
  placeholder = 'Начните вводить фамилию',
  id,
  invalid,
}: {
  value: string[];
  onChange: (ids: string[]) => void;
  multiple?: boolean;
  placeholder?: string;
  id?: string;
  invalid?: boolean;
}) {
  const options = useOptions();
  const listId = useId();
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const selected = value.map((v) => options.find((o) => o.userId === v)).filter(Boolean) as AssigneeOption[];
  const filtered = useMemo(
    () =>
      options.filter((o) => (multiple ? !value.includes(o.userId) : true) && matches(o, text)).slice(0, 50),
    [options, text, value, multiple],
  );

  const pick = (o: AssigneeOption) => {
    onChange(multiple ? [...value, o.userId] : [o.userId]);
    setText('');
    setActive(0);
    if (!multiple) {
      setOpen(false);
      input.current?.blur();
    }
  };

  const single = !multiple ? selected[0] : undefined;

  return (
    <div className="relative">
      <div
        className={clsx(
          'input flex h-auto min-h-9 flex-wrap items-center gap-1.5 py-1',
          invalid && 'border-danger',
          open && 'border-graphite',
        )}
      >
        {multiple &&
          selected.map((o) => (
            <span
              key={o.userId}
              className="inline-flex items-center gap-1 rounded bg-sunken py-0.5 pl-1 pr-1 text-[13px]"
            >
              <Avatar name={o.fullName} userId={o.userId} size={18} />
              {o.fullName}
              <button
                type="button"
                aria-label={`Убрать ${o.fullName}`}
                className="text-steel hover:text-danger"
                onClick={() => onChange(value.filter((v) => v !== o.userId))}
              >
                <X className="size-3.5" />
              </button>
            </span>
          ))}
        <input
          ref={input}
          id={id}
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          value={open || !single ? text : single.fullName}
          placeholder={single && !open ? undefined : placeholder}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 120)}
          onChange={(e) => {
            setText(e.target.value);
            setActive(0);
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setActive((a) => Math.min(a + 1, filtered.length - 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setActive((a) => Math.max(a - 1, 0));
            } else if (e.key === 'Enter' && open && filtered[active]) {
              e.preventDefault();
              pick(filtered[active]);
            } else if (e.key === 'Backspace' && !text && multiple && value.length) {
              onChange(value.slice(0, -1));
            }
          }}
          className="min-w-[120px] flex-1 bg-transparent outline-none placeholder:text-steel/70"
        />
      </div>
      {open && (
        <div
          id={listId}
          role="listbox"
          className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-md border border-line-strong bg-surface py-1 shadow-[0_10px_30px_-12px_rgb(24_34_43/0.35)]"
        >
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-steel">Никого не нашли</div>
          ) : (
            filtered.map((o, i) => (
              <div
                key={o.userId}
                role="option"
                tabIndex={-1}
                aria-selected={i === active}
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(o);
                }}
                onMouseEnter={() => setActive(i)}
                className={clsx(
                  'flex cursor-pointer items-center gap-2.5 px-3 py-1.5',
                  i === active && 'bg-sunken',
                )}
              >
                <Avatar name={o.fullName} userId={o.userId} size={24} />
                <span className="min-w-0">
                  <span className="block truncate">{o.fullName}</span>
                  <span className="block truncate text-[12px] text-steel">
                    {[o.positionName, o.departmentName].filter(Boolean).join(', ')}
                  </span>
                </span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
